import type { NicknameStatus } from "@golazo/shared";
import { t } from "../i18n/index.js";
import { NICK_MAX, type NicknameService } from "../online/nickname.js";
import "./nicknameDialog.css";

/**
 * Cambiar de apodo, desde donde sea: el lobby, el menu o personalizar.
 *
 * Mientras escribes te dice si esta libre. Los apodos son unicos y la
 * comparacion no mira mayusculas, tildes ni espacios ("Juan Carlos" y
 * "juancarlos" son el mismo), asi que decirlo ANTES de pulsar es lo que evita
 * la frustracion de pulsar, esperar y leer "ya lo tiene otro".
 */
export interface NicknameDialog {
  /** Resuelve con el apodo nuevo, o null si se cierra sin cambiarlo. */
  open(notice?: string): Promise<string | null>;
  dispose(): void;
}

const CHECK_DELAY_MS = 280;

function statusText(status: NicknameStatus | "unknown" | "checking" | "empty"): string {
  switch (status) {
    case "free":
      return t("✓ Libre. Es tuyo si lo guardas.");
    case "yours":
      return t("Es el que ya tienes.");
    case "taken":
      return t("✗ Ya lo tiene otro jugador. Prueba con otro (un número también vale).");
    case "invalid":
      return t("✗ Ese no vale. Prueba con otro.");
    case "checking":
      return t("Comprobando…");
    case "empty":
      return "";
    default:
      return t("Sin conexión: se comprobará al conectar.");
  }
}

export function createNicknameDialog(service: NicknameService, root: HTMLElement = document.body): NicknameDialog {
  const overlay = document.createElement("div");
  overlay.className = "nick-dialog is-hidden";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-labelledby", "nick-dialog-title");
  overlay.innerHTML = `
    <button type="button" class="nick-dialog-backdrop" data-nd-cancel tabindex="-1" aria-label="${t("Cerrar")}"></button>
    <form class="nick-dialog-sheet" data-nd-form novalidate>
      <h2 id="nick-dialog-title">${t("Tu apodo")}</h2>
      <p class="nick-dialog-notice" data-nd-notice hidden></p>
      <label class="nick-dialog-field">
        <span class="nick-dialog-at" aria-hidden="true">@</span>
        <input
          type="text"
          data-nd-input
          maxlength="${NICK_MAX}"
          autocomplete="nickname"
          autocapitalize="off"
          spellcheck="false"
          placeholder="${t("tu nombre")}"
          aria-describedby="nick-dialog-status"
        />
      </label>
      <p class="nick-dialog-status" id="nick-dialog-status" data-nd-status aria-live="polite"></p>
      <p class="nick-dialog-fine">${t("Es único: nadie más puede llamarse igual. Mayúsculas, tildes y espacios no cuentan como diferencia.")}</p>
      <div class="nick-dialog-actions">
        <button type="button" class="nick-dialog-btn" data-nd-cancel>${t("Cancelar")}</button>
        <button type="submit" class="nick-dialog-btn is-primary" data-nd-save>${t("Guardar")}</button>
      </div>
    </form>
  `;
  root.appendChild(overlay);

  const form = overlay.querySelector("[data-nd-form]") as HTMLFormElement;
  const input = overlay.querySelector("[data-nd-input]") as HTMLInputElement;
  const statusOut = overlay.querySelector("[data-nd-status]") as HTMLElement;
  const noticeOut = overlay.querySelector("[data-nd-notice]") as HTMLElement;
  const saveBtn = overlay.querySelector("[data-nd-save]") as HTMLButtonElement;

  let resolveOpen: ((value: string | null) => void) | null = null;
  let checkTimer = 0;
  /** Solo cuenta la respuesta de lo ULTIMO que se escribio. */
  let checkSeq = 0;
  let saving = false;

  const setStatus = (status: Parameters<typeof statusText>[0]): void => {
    statusOut.textContent = statusText(status);
    statusOut.dataset.status = status;
    saveBtn.disabled = saving || status === "taken" || status === "invalid" || status === "empty";
  };

  const scheduleCheck = (): void => {
    window.clearTimeout(checkTimer);
    const value = input.value.trim();
    if (!value) {
      setStatus("empty");
      return;
    }
    setStatus("checking");
    const seq = ++checkSeq;
    checkTimer = window.setTimeout(() => {
      void service.check(value).then((status) => {
        if (seq === checkSeq) setStatus(status);
      });
    }, CHECK_DELAY_MS);
  };

  const close = (value: string | null): void => {
    window.clearTimeout(checkTimer);
    overlay.classList.add("is-hidden");
    const done = resolveOpen;
    resolveOpen = null;
    done?.(value);
  };

  input.addEventListener("input", scheduleCheck);
  for (const el of overlay.querySelectorAll("[data-nd-cancel]")) el.addEventListener("click", () => close(null));

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const value = input.value.trim();
    if (!value || saving) return;
    if (value === service.current()) {
      close(null);
      return;
    }
    saving = true;
    saveBtn.disabled = true;
    statusOut.textContent = t("Guardando…");
    void service.set(value).then((result) => {
      saving = false;
      if (result.ok) {
        close(result.nickname);
        return;
      }
      setStatus(result.reason === "taken" ? "taken" : result.reason === "invalid" ? "invalid" : "unknown");
      if (result.reason === "slow") statusOut.textContent = t("Más despacio: espera un momento.");
      if (result.reason === "timeout") statusOut.textContent = t("El servidor no contesta. Inténtalo de nuevo.");
      saveBtn.disabled = false;
    });
  });

  const onKey = (event: KeyboardEvent): void => {
    if (event.key === "Escape" && !overlay.classList.contains("is-hidden")) {
      event.stopPropagation();
      close(null);
    }
  };
  window.addEventListener("keydown", onKey, true);

  return {
    open(notice) {
      // Otro dialogo abierto: se cierra ese antes de abrir este.
      if (resolveOpen) close(null);
      input.value = service.current();
      noticeOut.hidden = !notice;
      noticeOut.textContent = notice ?? "";
      setStatus(input.value ? "yours" : "empty");
      saving = false;
      overlay.classList.remove("is-hidden");
      window.setTimeout(() => {
        input.focus();
        input.select();
      }, 0);
      return new Promise((resolve) => {
        resolveOpen = resolve;
      });
    },
    dispose() {
      close(null);
      window.removeEventListener("keydown", onKey, true);
      overlay.remove();
    },
  };
}
